import type { StatusPresencaAula } from '../../types/models';

export type AulaStatus = 'feita' | 'agora' | 'pendente' | 'perdida' | 'proxima' | 'vazia';

export const STATUS_LABELS: Record<StatusPresencaAula, { label: string; bg: string; color: string }> = {
  PRESENTE:    { label: 'P', bg: '#dcfce7', color: '#166534' },
  AUSENTE:     { label: 'F', bg: '#fee2e2', color: '#991b1b' },
  ATRASADO:    { label: 'A', bg: '#fef3c7', color: '#92400e' },
  JUSTIFICADO: { label: 'J', bg: '#e0e7ff', color: '#3730a3' },
  DISPENSADO:  { label: 'D', bg: '#f3e8ff', color: '#6b21a8' },
};

export const STATUS_CONFIG: Record<AulaStatus, { icon: string; label: string; color: string; bg: string }> = {
  feita:   { icon: '✅', label: 'Feita',    color: '#16a34a', bg: '#f0fdf4' },
  agora:   { icon: '🕐', label: 'Agora',    color: '#2563eb', bg: '#eff6ff' },
  pendente:{ icon: '⏳', label: 'Pendente', color: '#d97706', bg: '#fffbeb' },
  perdida: { icon: '⚠️', label: 'Fora do Horário', color: '#d97706', bg: '#fef3c7' },
  proxima: { icon: '⏰', label: 'Próxima', color: '#9ca3af', bg: '#f9fafb' },
  vazia:   { icon: '➖', label: 'Sem Alunos', color: '#6b7280', bg: '#f3f4f6' },
};

export const DIA_LABELS: Record<string, string> = {
  SEGUNDA: 'Segunda-feira', TERCA: 'Ter\u00e7a-feira', QUARTA: 'Quarta-feira',
  QUINTA: 'Quinta-feira', SEXTA: 'Sexta-feira', SABADO: 'S\u00e1bado',
};

/** Converte "HH:MM" em minutos desde meia-noite */
export function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Relógio → "HH:MM" (sim virtual ou wall). */
export function hmFromDate(d: Date): string {
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Slot vencido = passou `horaFim` (mesma regra da Governança / ChamadaGestao).
 * Antes disso não conta como "sem lançamento" no KPI da equipe.
 */
export function slotHorarioVenceu(
  horaFim: string | null | undefined,
  horaAtualHm: string,
): boolean {
  const fim = String(horaFim || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(fim)) return false;
  return horaAtualHm > fim;
}

/** Slot em curso no relógio (janela [inicio, fim]). */
export function slotHorarioEmCurso(
  horaInicio: string | null | undefined,
  horaFim: string | null | undefined,
  horaAtualHm: string,
): boolean {
  const ini = String(horaInicio || '').slice(0, 5);
  const fim = String(horaFim || '').slice(0, 5);
  if (!/^\d{2}:\d{2}$/.test(ini) || !/^\d{2}:\d{2}$/.test(fim)) return false;
  return horaAtualHm >= ini && horaAtualHm <= fim;
}

export type ChamadaMatchRef = {
  horarioAulaId?: string | null;
  turmaId?: string;
  materiaId?: string | null;
  numeroAula?: number | null;
  status?: string | null;
  totalAlunos?: number | null;
};

export type HorarioSlotRef = {
  id?: string;
  turmaId?: string;
  materiaId?: string | null;
  materia?: { id?: string } | null;
  numeroAula?: number | null;
  horaInicio?: string | null;
  horaFim?: string | null;
};

/** CONCLUIDA ou ABERTA/EM_ANDAMENTO com alunos lançados. */
export function chamadaEstaRegistrada(c?: {
  status?: string | null;
  totalAlunos?: number | null;
} | null): boolean {
  if (!c) return false;
  const st = String(c.status || '').toUpperCase();
  if (st === 'CONCLUIDA') return true;
  return (
    (st === 'ABERTA' || st === 'EM_ANDAMENTO') && (c.totalAlunos ?? 0) > 0
  );
}

export function chamadaEstaAbertaVazia(c?: {
  status?: string | null;
  totalAlunos?: number | null;
} | null): boolean {
  if (!c) return false;
  const st = String(c.status || '').toUpperCase();
  return (
    (st === 'ABERTA' || st === 'EM_ANDAMENTO') && (c.totalAlunos ?? 0) === 0
  );
}

/** Casa slot × chamada por horarioAulaId / turma+matéria / turma+numeroAula. */
export function chamadaCobreSlot(
  c: ChamadaMatchRef,
  h: HorarioSlotRef,
): boolean {
  if (c.horarioAulaId && h.id && c.horarioAulaId === h.id) return true;
  const matH = h.materiaId ?? h.materia?.id ?? null;
  if (
    c.turmaId &&
    h.turmaId &&
    c.turmaId === h.turmaId &&
    (c.materiaId ?? null) === (matH ?? null) &&
    (c.materiaId || matH)
  ) {
    return true;
  }
  if (
    c.turmaId &&
    h.turmaId &&
    c.turmaId === h.turmaId &&
    c.numeroAula != null &&
    h.numeroAula != null &&
    Number(c.numeroAula) === Number(h.numeroAula)
  ) {
    return true;
  }
  return false;
}

/**
 * Status operacional do slot (painel turno / governança).
 * - registrada: chamada com lançamento
 * - emCurso: ABERTA sem alunos
 * - janela: dentro de [inicio,fim] sem chamada
 * - perdida: passou horaFim sem chamada
 * - futuro: ainda não chegou o início
 */
export type SlotChamadaStatusOperacional =
  | 'registrada'
  | 'emCurso'
  | 'janela'
  | 'perdida'
  | 'futuro';

export function classificarSlotChamadaOperacional(
  slot: HorarioSlotRef,
  chamada: ChamadaMatchRef | null | undefined,
  horaAtualHm: string,
): SlotChamadaStatusOperacional {
  if (chamada && chamadaEstaRegistrada(chamada)) return 'registrada';
  if (chamada && chamadaEstaAbertaVazia(chamada)) return 'emCurso';
  if (slotHorarioVenceu(slot.horaFim, horaAtualHm)) return 'perdida';
  if (slotHorarioEmCurso(slot.horaInicio, slot.horaFim, horaAtualHm)) {
    return 'janela';
  }
  return 'futuro';
}

/** Compat Governança: janela+futuro → pendente. */
export function classificarSlotChamadaGestao(
  slot: HorarioSlotRef,
  chamada: ChamadaMatchRef | null | undefined,
  horaAtualHm: string,
): 'registrada' | 'emCurso' | 'perdida' | 'pendente' {
  const st = classificarSlotChamadaOperacional(slot, chamada, horaAtualHm);
  if (st === 'registrada') return 'registrada';
  if (st === 'emCurso') return 'emCurso';
  if (st === 'perdida') return 'perdida';
  return 'pendente';
}

export type TurnoChamadaOpts = {
  toleranciaChamadaMin?: number | null;
  janelaExtraChamadaMin?: number | null;
};

export type JanelaChamadaResult =
  | { ok: true; requerAuditoria?: boolean; message?: string }
  | { ok: false; limite: string; message: string };

/** Espelha `validarJanelaChamadaProfessor` do backend (chamadas.service). */
export function avaliarJanelaChamadaProfessor(
  horaInicio: string | undefined,
  horaFim: string | undefined,
  turnoOp?: TurnoChamadaOpts | null,
  opts?: { foraDoHorario?: boolean; agora?: Date },
): JanelaChamadaResult {
  if (!horaInicio || !horaFim) return { ok: true };

  const now = opts?.agora ?? new Date();
  const hhmm = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const tolerancia = turnoOp?.toleranciaChamadaMin ?? 10;
  const janelaExtra = turnoOp?.janelaExtraChamadaMin ?? 60;

  const inicioMin = toMinutes(horaInicio) - 5;
  const inicioComAntecedencia = `${String(Math.floor(Math.max(0, inicioMin) / 60)).padStart(2, '0')}:${String(Math.max(0, inicioMin) % 60).padStart(2, '0')}`;

  const [fH, fM] = horaFim.split(':').map(Number);
  const fimComJanela = fH * 60 + fM + tolerancia + janelaExtra;
  const fimJanelaFormatado = `${String(Math.floor(fimComJanela / 60)).padStart(2, '0')}:${String(fimComJanela % 60).padStart(2, '0')}`;

  if (toMinutes(hhmm) < toMinutes(inicioComAntecedencia)) {
    return {
      ok: false,
      limite: horaInicio,
      message: `Chamada disponível a partir das ${horaInicio} (até 5 min antes). Aguarde o início da aula.`,
    };
  }

  if (toMinutes(hhmm) > fimComJanela) {
    return {
      ok: true,
      requerAuditoria: true,
      message: `Janela de chamada expirou (limite: ${fimJanelaFormatado}). A chamada será permitida e encaminhada para auditoria da coordenação.`,
    };
  }

  return { ok: true };
}

/** Resolve turno operacional da aula (horário → turma na lista). */
export function turnoOperacionalDaAula(
  aula: { turmaId: string; turma?: { turnoOperacional?: TurnoChamadaOpts } },
  turmas: Array<{ id: string; turnoOperacional?: TurnoChamadaOpts }>,
): TurnoChamadaOpts | undefined {
  return aula.turma?.turnoOperacional ?? turmas.find((t) => t.id === aula.turmaId)?.turnoOperacional;
}

export type FaseTurnoChamada = 'passado' | 'agora' | 'futuro';

/** Normaliza rótulo de turno para Manhã | Tarde | Noite. */
export function normalizarLabelTurno(raw?: string | null): string {
  const n = String(raw || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase();
  if (n.includes('MANHA') || n.includes('MATUT')) return 'Manhã';
  if (n.includes('TARDE') || n.includes('VESP')) return 'Tarde';
  if (n.includes('NOITE') || n.includes('NOTUR')) return 'Noite';
  return String(raw || 'Sem turno').trim() || 'Sem turno';
}

type TurnoJanela = {
  label: string;
  abertura: string;
  fechamento: string;
};

/**
 * No máximo 1 turno "AGORA": janela [aberturaPortões, fechamentoPortões].
 * Sem janela cadastrada → não força exclusividade (retorna mapa vazio).
 */
export function resolverFaseTurnosPorRelogio(
  horaAtualHm: string,
  turmas: Array<{
    turno?: string | null;
    turnoOperacional?: {
      nome?: string | null;
      horaAberturaPortoes?: string | null;
      horaFechamentoPortoes?: string | null;
      horaSaidaOficial?: string | null;
    } | null;
  }>,
): Map<string, FaseTurnoChamada> {
  const janelas = new Map<string, TurnoJanela>();
  for (const t of turmas) {
    const label = normalizarLabelTurno(t.turnoOperacional?.nome || t.turno);
    if (janelas.has(label)) continue;
    const ab = String(t.turnoOperacional?.horaAberturaPortoes || '').slice(0, 5);
    const fc = String(
      t.turnoOperacional?.horaFechamentoPortoes ||
        t.turnoOperacional?.horaSaidaOficial ||
        '',
    ).slice(0, 5);
    if (!ab || !fc) continue;
    janelas.set(label, { label, abertura: ab, fechamento: fc });
  }

  const out = new Map<string, FaseTurnoChamada>();
  if (janelas.size === 0) return out;

  const agoraMin = toMinutes(horaAtualHm);
  const ordem = ['Manhã', 'Tarde', 'Noite'];
  // Empate/overlap: no máximo 1 AGORA, preferindo Manhã → Tarde → Noite
  let ativo: string | null = null;
  for (const label of ordem) {
    const j = janelas.get(label);
    if (!j) continue;
    if (agoraMin >= toMinutes(j.abertura) && agoraMin <= toMinutes(j.fechamento)) {
      ativo = label;
      break;
    }
  }
  if (!ativo) {
    for (const [label, j] of janelas) {
      if (ordem.includes(label)) continue;
      if (agoraMin >= toMinutes(j.abertura) && agoraMin <= toMinutes(j.fechamento)) {
        ativo = label;
        break;
      }
    }
  }

  for (const [label, j] of janelas) {
    if (ativo && label === ativo) out.set(label, 'agora');
    else if (agoraMin < toMinutes(j.abertura)) out.set(label, 'futuro');
    else out.set(label, 'passado');
  }
  return out;
}

/**
 * Combina janela do relógio com a grade do dia.
 * Evita "FUTURO" com 5/5 concluídas (sim antecipada) e mantém no máx. 1 AGORA na UI.
 */
export function combinarFaseTurnoComGrade(
  faseRelogio: FaseTurnoChamada | undefined,
  stats: {
    total: number;
    feitas: number;
    emAula: number;
    proximas: number;
    perdidas: number;
    vazias: number;
  },
): FaseTurnoChamada {
  const { total, feitas, emAula, proximas, perdidas, vazias } = stats;
  if (total === 0) return faseRelogio ?? 'futuro';

  const gradeEncerrada = feitas + perdidas + vazias === total && emAula === 0;
  if (gradeEncerrada) return 'passado';

  if (emAula > 0) return 'agora';

  const rel = faseRelogio;
  if (rel === 'agora') return 'agora';
  if (rel === 'passado') return 'passado';

  // Relógio ainda não abriu o turno…
  if (feitas === 0 && proximas > 0) return 'futuro';
  // …mas a sim/coordenação já lançou chamadas → não rotular como futuro.
  if (feitas > 0 && proximas > 0) return 'agora';
  if (feitas > 0) return 'passado';

  return rel ?? 'futuro';
}

/** Formata segundos em formato legível para countdown.
 * - Abaixo de 1h:     "MM:SS"  (ex: 12:45)
 * - 1h a 24h:         "Xh YYmin" (ex: 2h 30min)
 * - Acima de 24h:     "Xd Yh"   (ex: 1d 14h)
 */
export function fmtTimer(totalSec: number): string {
  if (totalSec <= 0) return '00:00';
  const dias = Math.floor(totalSec / 86400);
  const horas = Math.floor((totalSec % 86400) / 3600);
  const minutos = Math.floor((totalSec % 3600) / 60);
  const segundos = totalSec % 60;

  if (dias > 0) {
    return horas > 0 ? `${dias}d ${horas}h` : `${dias}d`;
  }
  if (horas > 0) {
    return minutos > 0 ? `${horas}h ${minutos}min` : `${horas}h`;
  }
  return `${String(minutos).padStart(2, '0')}:${String(segundos).padStart(2, '0')}`;
}

/** Retorna iniciais do nome (max 2 letras) */
export function getIniciais(nome: string): string {
  return nome.split(' ').filter(Boolean).slice(0, 2).map(n => n[0]).join('').toUpperCase();
}

type AlunoTurmaRef = { turmaId: string; atual?: boolean };

/** Aluno ativo na turma (turmaId direto ou histórico marcado como atual). */
export function alunoPertenceTurma(
  aluno: { turmaId?: string | null; ativo?: boolean; historicoTurmas?: AlunoTurmaRef[] },
  turmaId: string,
): boolean {
  if (aluno.ativo === false) return false;
  if (aluno.turmaId === turmaId) return true;
  return (aluno.historicoTurmas ?? []).some((h) => h.turmaId === turmaId && h.atual === true);
}

/** Conta alunos ativos vinculados à turma (mesma regra do backend de chamadas). */
export function countAlunosNaTurma(
  alunos: Array<{ turmaId?: string | null; ativo?: boolean; historicoTurmas?: AlunoTurmaRef[] }>,
  turmaId: string,
): number {
  if (!turmaId) return 0;
  return alunos.filter((a) => alunoPertenceTurma(a, turmaId)).length;
}

type TurmaRef = { id: string; nome?: string; serie?: string };

/**
 * Quando a turma do horário está vazia, sugere outras turmas da escola que têm alunos
 * (mesma série ou nome parecido) — ajuda a detectar vínculo na turma errada.
 */
export type ChamadaSlotInfo = {
  status: string;
  totalPresentes?: number;
  totalAusentes?: number;
  totalAlunos?: number;
};

export type AulaStatusContext = {
  dataReferencia: string;
  chamadaInfo?: ChamadaSlotInfo;
  horarioAtualId?: string;
  horaAtual: string;
  qtdAlunos: number;
  loadingAlunos: boolean;
};

/** Status da aula respeitando o dia selecionado (calendário desktop). */
export function getAulaStatusParaData(
  slot: { id: string; horaInicio?: string; horaFim?: string },
  ctx: AulaStatusContext,
): AulaStatus {
  if (!ctx.loadingAlunos && ctx.qtdAlunos === 0) return 'vazia';
  const st = String(ctx.chamadaInfo?.status || '').toUpperCase();
  if (st === 'CONCLUIDA') return 'feita';
  // Alinha com Dashboard: ABERTA com lançamento = registrada/feita
  if (st === 'ABERTA' && (ctx.chamadaInfo?.totalAlunos ?? 0) > 0) return 'feita';
  if (st === 'ABERTA') return 'agora';

  const hojeIso = new Date().toISOString().split('T')[0];
  if (ctx.dataReferencia < hojeIso) return 'perdida';
  if (ctx.dataReferencia > hojeIso) return 'proxima';

  if (ctx.horarioAtualId === slot.id) return 'agora';
  if (slot.horaFim && ctx.horaAtual > slot.horaFim) return 'perdida';
  if (slot.horaInicio && ctx.horaAtual >= slot.horaInicio) return 'agora';
  return 'proxima';
}

export function turmasComAlunosSugeridas(
  turmaAtual: TurmaRef | undefined,
  turmas: TurmaRef[],
  alunos: Array<{ turmaId?: string | null; ativo?: boolean; historicoTurmas?: AlunoTurmaRef[] }>,
  max = 3,
): Array<{ id: string; nome: string; total: number }> {
  if (!turmaAtual?.id) return [];
  const serie = (turmaAtual.serie || '').trim().toLowerCase();
  const nomeBase = (turmaAtual.nome || '').trim().toLowerCase();

  const candidatas = turmas
    .filter((t) => t.id !== turmaAtual.id)
    .map((t) => ({
      id: t.id,
      nome: t.nome || 'Turma',
      total: countAlunosNaTurma(alunos, t.id),
    }))
    .filter((t) => t.total > 0)
    .sort((a, b) => {
      const ta = turmas.find((x) => x.id === a.id);
      const tb = turmas.find((x) => x.id === b.id);
      const score = (t: TurmaRef | undefined) => {
        let s = 0;
        const n = (t?.nome || '').toLowerCase();
        const se = (t?.serie || '').toLowerCase();
        if (serie && se === serie) s += 3;
        if (nomeBase && n.includes(nomeBase.split(' ')[0])) s += 2;
        if (nomeBase && nomeBase.includes(n.split(' ')[0])) s += 1;
        return s;
      };
      const diff = score(tb) - score(ta);
      return diff !== 0 ? diff : b.total - a.total;
    });

  return candidatas.slice(0, max);
}
